import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Certification, Education, Experience, Portfolio, Resume } from "../../../../Types/Resume";
import { removeUnwantedCharacters } from "../../../../Utils/String";

interface TalentProfileCreationState {
    userId: string | undefined;
    step: number;
    primaryLanguage: string | undefined;
    secondaryLanguage: string | undefined;
    summary: string | undefined;
    primaryExpertise: string | undefined;
    secondaryExpertise: string | undefined;
    tertiaryExpertise: string | undefined;
    expertiseLevel: string | undefined;
    education: Education[];
    degree: string | undefined;
    major: string | undefined;
    school: string | undefined;
    workExperience: Experience[];
    certifications: Certification[];
    portfolio: Portfolio[];
    industry: string | undefined;
    workSite: string | undefined;
    hasResume: boolean;
    nextDisabled: boolean;
    profilePicture: File | undefined;
}

const initialState: TalentProfileCreationState = {
    userId: undefined,
    step: 1,
    primaryLanguage: undefined,
    secondaryLanguage: undefined,
    summary: undefined,
    primaryExpertise: undefined,
    secondaryExpertise: undefined,
    tertiaryExpertise: undefined,
    expertiseLevel: undefined,
    education: [],
    degree: undefined,
    major: undefined,
    school: undefined,
    workExperience: [],
    certifications: [],
    portfolio: [],
    industry: undefined,
    workSite: undefined,
    hasResume: false,
    nextDisabled: true,
    profilePicture: undefined
}

export const talentProfileCreationSlice = createSlice({
    name: 'TalentProfileCreation',
    initialState,
    reducers: {
        setProfileCreationUserId(state, action: PayloadAction<TalentProfileCreationState["userId"]>){
            state.userId = action.payload;
        },
        processParsedResume(state, action: PayloadAction<Resume>){
            // @ts-ignore
            if (action.payload.urls!.length > 0) state.portfolio = action.payload.urls!.map( (url, idx) => ({_id: -idx, type: url.type, url: url.url}))
            if (Array.isArray(action.payload.languages) && action.payload.languages.length > 0) {
                state.primaryLanguage = action.payload.languages[0].name
                state.secondaryLanguage = action.payload?.languages[1].name ?? undefined
            }
            if (action.payload.summary) state.summary = removeUnwantedCharacters(action.payload.summary);
            if (Array.isArray(action.payload.experience) && action.payload.experience) state.workExperience = action.payload.experience.map((exp, idx) => ({...exp, _id: -idx}));
            if (Array.isArray(action.payload.education) && action.payload.education) state.education = action.payload.education.map((edu, idx) => ({...edu, _id: -idx}))
            if (Array.isArray(action.payload.certifications) && action.payload.certifications) state.certifications = action.payload.certifications.map((cert, idx) => ({...cert, _id: -idx}))
        },
        setTalentProfileCreationStep(state, action: PayloadAction<TalentProfileCreationState["step"]>){
            state.step = action.payload;
        },
        setPrimaryLanguage(state, action: PayloadAction<TalentProfileCreationState["primaryLanguage"]>){
            state.primaryLanguage = action.payload;
        },
        setSecondaryLanguage(state, action: PayloadAction<TalentProfileCreationState["secondaryLanguage"]>){
            state.secondaryLanguage = action.payload;
        },
        setSummary(state, action: PayloadAction<TalentProfileCreationState["summary"]>){
            state.summary = action.payload;
        },
        setPrimaryExpertise(state, action: PayloadAction<TalentProfileCreationState["primaryExpertise"]>){
            state.primaryExpertise = action.payload;
        },
        setSecondaryExpertise(state, action: PayloadAction<TalentProfileCreationState["secondaryExpertise"]>){
            state.secondaryExpertise = action.payload;
        },
        setTertiaryExpertise(state, action: PayloadAction<TalentProfileCreationState["tertiaryExpertise"]>){
            state.tertiaryExpertise = action.payload;
        },
        setExpertiseLevel(state, action: PayloadAction<TalentProfileCreationState["expertiseLevel"]>){
            state.expertiseLevel = action.payload;
        },
        //Education
        setEducation(state, action: PayloadAction<TalentProfileCreationState["education"]>){
            state.education = action.payload;
        },
        addEducation(state, action: PayloadAction<Education>){
            state.education.push(action.payload)
        },
        updateEducation(state, action: PayloadAction<Education>){
            const expIdx = state.education.findIndex( exp => exp._id == action.payload._id)
            if (expIdx >= 0) state.education[expIdx] = {...state.education[expIdx], ...action.payload}
        },
        deleteEducation(state, action: PayloadAction<number>){
            const expIdx = state.education.findIndex( exp => exp._id === action.payload)
            state.education.splice(expIdx, 1);
        },
        //experience
        setWorkExperience(state, action: PayloadAction<TalentProfileCreationState["workExperience"]>){
            state.workExperience = action.payload;
        },
        addWorkExperience(state, action: PayloadAction<Experience>){
            state.workExperience.push(action.payload)
        },
        updateWorkExperience(state, action: PayloadAction<Experience>){
            const expIdx = state.workExperience.findIndex( exp => exp._id == action.payload._id)
            if (expIdx >= 0) state.workExperience[expIdx] = {...state.workExperience[expIdx], ...action.payload}
        },
        deleteWorkExperience(state, action: PayloadAction<number>){
            const expIdx = state.workExperience.findIndex( exp => exp._id === action.payload)
            state.workExperience.splice(expIdx, 1);
        },
        //certifications
        setCertifications(state, action: PayloadAction<TalentProfileCreationState["certifications"]>){
            state.certifications = action.payload;
        },
        addCertification(state, action: PayloadAction<Certification>){
            state.certifications.push(action.payload)
        },
        updateCertification(state, action: PayloadAction<Certification>){
            const expIdx = state.certifications.findIndex( exp => exp._id == action.payload._id)
            if (expIdx >= 0) state.certifications[expIdx] = {...state.certifications[expIdx], ...action.payload}
        },
        deleteCertification(state, action: PayloadAction<number>){
            const expIdx = state.certifications.findIndex( exp => exp._id === action.payload)
            state.certifications.splice(expIdx, 1);
        },
        //portfolio
        setPortfolio(state, action: PayloadAction<TalentProfileCreationState["portfolio"]>){
            state.portfolio = action.payload;
        },
        addPortfolio(state, action: PayloadAction<Portfolio>){
            state.portfolio.push(action.payload)
        },
        updatePortfolio(state, action: PayloadAction<Portfolio>){
            const expIdx = state.portfolio.findIndex( exp => exp._id == action.payload._id)
            if (expIdx >= 0) state.portfolio[expIdx] = {...state.portfolio[expIdx], ...action.payload}
        },
        deletePortfolio(state, action: PayloadAction<number>){
            const expIdx = state.portfolio.findIndex( exp => exp._id === action.payload)
            state.portfolio.splice(expIdx, 1);
        },
        setIndustry(state, action: PayloadAction<TalentProfileCreationState["industry"]>){
            state.industry = action.payload;
        },
        setWorkSite(state, action: PayloadAction<TalentProfileCreationState["workSite"]>){
            state.workSite = action.payload;
        },
        setHasResume(state, action: PayloadAction<TalentProfileCreationState["hasResume"]>){
            state.hasResume = action.payload;
        },
        setNextDisabled(state, action: PayloadAction<TalentProfileCreationState["nextDisabled"]>){
            state.nextDisabled = action.payload;
        },
        setProfilePicture(state, action: PayloadAction<TalentProfileCreationState["profilePicture"]>){
            state.profilePicture = action.payload;
        },
        resetTalentProfileCreationState: () => initialState
    }
})

export const {
    setSummary,
    setCertifications,
    setHasResume,
    setIndustry,
    setPortfolio,
    addPortfolio,
    updatePortfolio,
    deletePortfolio,
    setPrimaryLanguage,
    setEducation,
    addEducation,
    updateEducation,
    deleteEducation,
    setPrimaryExpertise,
    setSecondaryLanguage,
    setSecondaryExpertise,
    setExpertiseLevel,
    setTalentProfileCreationStep,
    setTertiaryExpertise,
    setWorkExperience,
    setWorkSite,
    setNextDisabled,
    addWorkExperience,
    updateWorkExperience,
    deleteWorkExperience,
    addCertification,
    updateCertification,
    deleteCertification,
    resetTalentProfileCreationState,
    processParsedResume,
    setProfileCreationUserId,
    setProfilePicture
} = talentProfileCreationSlice.actions

export default talentProfileCreationSlice.reducer

